import React, { useState } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  LockReset as LockResetIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ShoppingCart as ShoppingCartIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { authApi } from '@/api/auth';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useCartStore } from '@/stores/cartStore';
import { useSubscriptionGate } from '@/hooks/useSubscriptionGate';
import { CartDrawer } from '@/components/CartDrawer';
import { NotificationBell } from '@/components/NotificationBell';
import { brand } from '@/theme/theme';
import logoUrl from '@/assets/ardinotech-logo.png';
import type { RoleName } from '@/types';

interface NavItem {
  label: string;
  path: string;
  requireAny?: RoleName[];
}

// Wholesaler: Overview → Order Inbox → My Orders → Book Order → Network → REPS → More
const WHOLESALER_PRIMARY: NavItem[] = [
  { label: 'Overview', path: '/dashboard' },
  { label: 'Order Inbox', path: '/orders/inbox' },
  { label: 'My Orders', path: '/orders' },
  { label: 'Network', path: '/network' },
  { label: 'REPS', path: '/salesreps' },
];

const WHOLESALER_MORE: NavItem[] = [
  { label: 'Product Catalog', path: '/products' },
  { label: 'Analytics', path: '/reports' },
  { label: 'Billing & Plan', path: '/my-subscription' },
  { label: 'Subscription Admin', path: '/admin/subscription', requireAny: ['SUPER_ADMIN'] },
];

// Retailer: Overview → Purchase Orders → Book Order → Vendors → More
const RETAILER_PRIMARY: NavItem[] = [
  { label: 'Overview', path: '/dashboard' },
  { label: 'Purchase Orders', path: '/orders' },
  { label: 'Suppliers', path: '/network' },
];

const RETAILER_MORE: NavItem[] = [
  { label: 'Analytics', path: '/reports' },
];

// Representative: Overview → Sales Orders → Book Order → Suppliers → Retailers → More
const REPRESENTATIVE_PRIMARY: NavItem[] = [
  { label: 'Overview', path: '/dashboard' },
  { label: 'Purchase Orders', path: '/orders' },
  { label: 'Suppliers', path: '/salesreps' },
  { label: 'Retailers', path: '/rep-retailers' },
];

const REPRESENTATIVE_MORE: NavItem[] = [
  { label: 'Analytics', path: '/reports' },
];

// Super admin navigation
const SUPER_ADMIN_PRIMARY: NavItem[] = [
  { label: 'Dashboard',       path: '/dashboard' },
  { label: 'Retailers',       path: '/admin/tenants?type=RETAILER' },
  { label: 'Suppliers',       path: '/admin/tenants?type=WHOLESALER' },
  { label: 'Representatives', path: '/admin/tenants?type=REPRESENTATIVE' },
  { label: 'Sw. Vendors',     path: '/admin/vendors' },
];

const SUPER_ADMIN_MORE: NavItem[] = [
  { label: 'Registered Users', path: '/admin/tenants' },
  { label: 'Subscription Admin', path: '/admin/subscription' },
  { label: 'Create Vendor',    path: '/admin/tenants/new' },
];

// Fallback for other tenant types
const DEFAULT_PRIMARY: NavItem[] = [
  { label: 'My Orders', path: '/orders' },
  { label: 'My Products', path: '/products' },
];

const DEFAULT_MORE: NavItem[] = [
  { label: 'Dashboard', path: '/dashboard' },
];

const shine = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

function BrandMark({ onClick }: { onClick: () => void }) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      onClick={onClick}
      sx={{ cursor: 'pointer', minWidth: 0, flexShrink: 0 }}
    >
      <Box
        component="img"
        src={logoUrl}
        alt="Ardinotech"
        sx={{
          height: 64,
          width: 'auto',
          maxWidth: 160,
          objectFit: 'contain',
          filter: 'drop-shadow(0 2px 4px rgba(11, 21, 48, 0.12))',
        }}
      />
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', lineHeight: 1 }}>
        <Typography
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: brand.inkSoft,
            opacity: 0.85,
            mb: 0.25,
            whiteSpace: 'nowrap',
          }}
        >
          Ardinotech's
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Outfit", sans-serif',
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: '-0.01em',
            lineHeight: 1,
            whiteSpace: 'nowrap',
            backgroundImage: `linear-gradient(
              100deg,
              ${brand.navy} 0%,
              ${brand.violet} 30%,
              #FFFFFF 50%,
              ${brand.violetSoft} 70%,
              ${brand.navy} 100%
            )`,
            backgroundSize: '220% 100%',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
            filter: `drop-shadow(0 2px 6px ${brand.violetGlow})`,
            animation: `${shine} 4.5s ease-in-out infinite`,
          }}
        >
          Interface
        </Typography>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Change Password Dialog
// ---------------------------------------------------------------------------
function ChangePasswordDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

  // Per-field front-end validation
  const newPwdError = newPwd && !PASSWORD_REGEX.test(newPwd)
    ? 'Min 8 chars with uppercase, lowercase, digit & special character'
    : newPwd && newPwd === oldPwd
    ? 'New password must differ from your current password'
    : '';
  const confirmError = confirmPwd && confirmPwd !== newPwd ? 'Passwords do not match' : '';
  const canSubmit = oldPwd && newPwd && confirmPwd && !newPwdError && !confirmError && !loading;

  const handleClose = () => {
    setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    setError(''); setSuccess(false);
    setShowOld(false); setShowNew(false); setShowConfirm(false);
    onClose();
  };

  const handleSubmit = async () => {
    setError(''); setSuccess(false); setLoading(true);
    try {
      await authApi.changePassword({ oldPassword: oldPwd, newPassword: newPwd, confirmPassword: confirmPwd });
      setSuccess(true);
      setTimeout(handleClose, 1500);
    } catch (err: unknown) {
      // API client normalizes errors to { message, errorCode, ... } via normalizeError()
      const msg = (err as { message?: string })?.message;
      setError(msg || 'Failed to change password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const eyeBtn = (show: boolean, toggle: () => void) => (
    <InputAdornment position="end">
      <IconButton size="small" onClick={toggle} edge="end">
        {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16, pb: 0.5 }}>Reset Password</DialogTitle>
      <DialogContent sx={{ pt: '12px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <Alert severity="error" sx={{ fontSize: 13 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ fontSize: 13 }}>Password changed successfully!</Alert>}

        <TextField
          label="Current Password" type={showOld ? 'text' : 'password'}
          size="small" fullWidth value={oldPwd}
          onChange={e => setOldPwd(e.target.value)}
          InputProps={{ endAdornment: eyeBtn(showOld, () => setShowOld(v => !v)) }}
        />

        <Box>
          <TextField
            label="New Password" type={showNew ? 'text' : 'password'}
            size="small" fullWidth value={newPwd}
            onChange={e => setNewPwd(e.target.value)}
            error={Boolean(newPwdError)}
            helperText={newPwdError}
            InputProps={{ endAdornment: eyeBtn(showNew, () => setShowNew(v => !v)) }}
          />
        </Box>

        <TextField
          label="Confirm New Password" type={showConfirm ? 'text' : 'password'}
          size="small" fullWidth value={confirmPwd}
          onChange={e => setConfirmPwd(e.target.value)}
          error={Boolean(confirmError)}
          helperText={confirmError}
          InputProps={{ endAdornment: eyeBtn(showConfirm, () => setShowConfirm(v => !v)) }}
        />

        <Box sx={{ bgcolor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 1, px: 1.5, py: 1 }}>
          <Typography sx={{ fontSize: 12, color: '#92400e', fontWeight: 600, mb: 0.25 }}>
            📝 Note
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#78350f', lineHeight: 1.6 }}>
            You cannot reuse any of your last <strong>3 passwords</strong>. Password must be at least
            8 characters and include uppercase, lowercase, a digit and a special character.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button size="small" onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          size="small" variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
          sx={{ fontWeight: 700, minWidth: 130 }}
        >
          {loading ? 'Changing…' : 'Change Password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function AppLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [bookOrderAnchor, setBookOrderAnchor] = useState<HTMLElement | null>(null);
  const [myOrdersAnchor, setMyOrdersAnchor] = useState<HTMLElement | null>(null);
  const [cartOpen, setCartOpen] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasAnyRole } = useAuthStore();
  const cartItemCount    = useCartStore((s) => s.items.length);
  const loadCartFromBack = useCartStore((s) => s.loadFromBackend);

  // Load cart from backend once when the authenticated layout mounts
  React.useEffect(() => { loadCartFromBack(); }, [loadCartFromBack]);

  const [primaryNav, moreNav] = (() => {
    if (hasAnyRole(['SUPER_ADMIN'])) return [SUPER_ADMIN_PRIMARY, SUPER_ADMIN_MORE];
    if (user?.tenantType === 'WHOLESALER') return [WHOLESALER_PRIMARY, WHOLESALER_MORE];
    if (user?.tenantType === 'REPRESENTATIVE') return [REPRESENTATIVE_PRIMARY, REPRESENTATIVE_MORE];
    if (user?.tenantType === 'RETAILER') return [RETAILER_PRIMARY, RETAILER_MORE];
    return [DEFAULT_PRIMARY, DEFAULT_MORE];
  })();

  const visiblePrimary = primaryNav.filter(item => !item.requireAny || hasAnyRole(item.requireAny));
  const visibleMore = moreNav.filter(item => !item.requireAny || hasAnyRole(item.requireAny));

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNav = (path: string) => {
    navigate(path);
    setMobileOpen(false);
    setMoreAnchor(null);
  };

  const handleBookOrder = (tab: number) => {
    sessionStorage.setItem('myOrdersTab', String(tab));
    navigate('/orders', { state: { bookOrderTab: tab, ts: Date.now() } });
    setBookOrderAnchor(null);
    setMobileOpen(false);
  };

  const showBookOrder = true;
  const showMyOrdersDropdown = user?.tenantType === 'RETAILER' || user?.tenantType === 'REPRESENTATIVE';

  function handleMyOrdersNav(view: 'date' | 'supplier' | 'product') {
    sessionStorage.setItem('myOrdersTab', '0');
    sessionStorage.setItem('myOrdersView', view);
    navigate('/orders', { state: { bookOrderTab: 0, myOrdersView: view, ts: Date.now() } });
    setMyOrdersAnchor(null);
    setMobileOpen(false);
  }

  // Determine which tab is active on /orders to highlight My Orders vs Book Order independently
  const ordersTab = (() => {
    if (!isActive('/orders') || location.pathname.startsWith('/orders/inbox') || location.pathname.startsWith('/orders/received')) return -1;
    const state = location.state as { bookOrderTab?: number } | null;
    if (state?.bookOrderTab != null) return state.bookOrderTab;
    const saved = sessionStorage.getItem('myOrdersTab');
    return saved != null ? parseInt(saved, 10) : 0;
  })();
  const myOrdersNavActive = ordersTab === 0;
  const bookOrderNavActive = ordersTab === 1 || ordersTab === 2;

  const handleLogout = () => {
    setAccountAnchor(null);
    localStorage.removeItem('sub_reminder_last_shown');
    logout();
    navigate('/login');
  };

  // ── Subscription reminder (WHOLESALER only) ──────────────────────────────
  const isWholesaler = user?.tenantType === 'WHOLESALER';
  const [subReminderOpen, setSubReminderOpen] = useState(false);
  const SUB_REMINDER_LS_KEY = 'sub_reminder_last_shown';
  const REMINDER_INTERVAL_MS = 12 * 60 * 60 * 1000; // 12 h → twice per day

  const subGate = useSubscriptionGate(
    // Blocked → always show (hourly interval drives re-check).
    // Not blocked → show at most every 12 h (twice per day).
    React.useCallback((isBlocked?: boolean) => {
      if (!isWholesaler) return;
      if (isBlocked) {
        setSubReminderOpen(true);
        return;
      }
      const last = parseInt(localStorage.getItem(SUB_REMINDER_LS_KEY) ?? '0', 10);
      if (Date.now() - last > REMINDER_INTERVAL_MS) {
        setSubReminderOpen(true);
      }
    }, [isWholesaler]),
  );

  // Hourly refresh so the popup re-appears even if the user stays logged in
  React.useEffect(() => {
    if (!isWholesaler) return;
    const id = setInterval(() => subGate.refresh(), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, [isWholesaler, subGate.refresh]);

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        sx={{
          background: brand.glassBg,
          backdropFilter: 'blur(18px) saturate(180%)',
          WebkitBackdropFilter: 'blur(18px) saturate(180%)',
          boxShadow: '0 2px 12px -8px rgba(11, 21, 48, 0.08)',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${brand.navy} 0%, ${brand.violet} 35%, ${brand.violetSoft} 50%, ${brand.violet} 65%, ${brand.navy} 100%)`,
            opacity: 0.75,
          },
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 51, md: 58 }, gap: 1.5 }}>
          {/* Mobile hamburger */}
          {isMobile && (
            <IconButton
              edge="start"
              onClick={() => setMobileOpen(true)}
              sx={{ color: brand.navy }}
              aria-label="open navigation"
            >
              <MenuIcon />
            </IconButton>
          )}

          <BrandMark onClick={() => navigate('/dashboard')} />

          {/* Desktop top-bar nav */}
          {!isMobile && (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                ml: 3,
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                flexWrap: 'nowrap',
              }}
            >
              {visiblePrimary.map((item) => {
                const active = item.path === '/orders' ? myOrdersNavActive : isActive(item.path);
                const navBtnSx = {
                  color: active ? brand.violet : brand.navy,
                  fontWeight: active ? 700 : 600,
                  fontSize: 12,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase' as const,
                  px: 1,
                  py: 0,
                  minHeight: { xs: 51, md: 58 },
                  borderRadius: 0,
                  borderBottom: active ? `2px solid ${brand.violet}` : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  '&:hover': { background: 'transparent', color: brand.violet },
                };
                return (
                  <React.Fragment key={item.path}>
                    {item.path === '/orders' && showMyOrdersDropdown ? (
                      <>
                        <Button
                          endIcon={<ExpandMoreIcon />}
                          onClick={(e) => setMyOrdersAnchor(e.currentTarget)}
                          sx={{
                            ...navBtnSx,
                            color: (myOrdersNavActive || Boolean(myOrdersAnchor)) ? brand.violet : brand.navy,
                            fontWeight: (myOrdersNavActive || Boolean(myOrdersAnchor)) ? 700 : 600,
                            borderBottom: (myOrdersNavActive || Boolean(myOrdersAnchor))
                              ? `2px solid ${brand.violet}` : '2px solid transparent',
                          }}
                        >
                          My Orders
                        </Button>
                        <Menu
                          anchorEl={myOrdersAnchor}
                          open={Boolean(myOrdersAnchor)}
                          onClose={() => setMyOrdersAnchor(null)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                          <MenuItem onClick={() => handleMyOrdersNav('date')}>Date View</MenuItem>
                          <MenuItem onClick={() => handleMyOrdersNav('supplier')}>Supplier View</MenuItem>
                          <MenuItem onClick={() => handleMyOrdersNav('product')}>Product View</MenuItem>
                        </Menu>
                      </>
                    ) : (
                      <Button
                        onClick={() => {
                          if (item.path === '/orders') handleBookOrder(0);
                          else handleNav(item.path);
                        }}
                        sx={navBtnSx}
                      >
                        {item.label}
                      </Button>
                    )}
                    {item.path === '/orders' && showBookOrder && (
                      <>
                        <Button
                          onClick={(e) => setBookOrderAnchor(e.currentTarget)}
                          endIcon={<ExpandMoreIcon />}
                          sx={{
                            color: (bookOrderNavActive || Boolean(bookOrderAnchor)) ? brand.violet : brand.navy,
                            fontWeight: (bookOrderNavActive || Boolean(bookOrderAnchor)) ? 700 : 600,
                            fontSize: 12,
                            letterSpacing: '0.02em',
                            textTransform: 'uppercase',
                            px: 1,
                            py: 0,
                            minHeight: { xs: 51, md: 58 },
                            borderRadius: 0,
                            borderBottom: (bookOrderNavActive || Boolean(bookOrderAnchor))
                              ? `2px solid ${brand.violet}`
                              : '2px solid transparent',
                            whiteSpace: 'nowrap',
                            '&:hover': { background: 'transparent', color: brand.violet },
                          }}
                        >
                          Book Order
                        </Button>
                        <Menu
                          anchorEl={bookOrderAnchor}
                          open={Boolean(bookOrderAnchor)}
                          onClose={() => setBookOrderAnchor(null)}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                          <MenuItem onClick={() => handleBookOrder(1)}>Place Order</MenuItem>
                          <MenuItem onClick={() => handleBookOrder(2)}>Order Templates</MenuItem>
                        </Menu>
                      </>
                    )}
                  </React.Fragment>
                );
              })}
              {visibleMore.length > 0 && (
                <Button
                  onClick={(e) => setMoreAnchor(e.currentTarget)}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    color: brand.navy,
                    fontWeight: 600,
                    fontSize: 12,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    px: 1,
                    py: 0,
                    minHeight: { xs: 58, md: 64 },
                    borderRadius: 0,
                    '&:hover': { background: 'transparent', color: brand.violet },
                  }}
                >
                  More
                </Button>
              )}
              <Menu
                anchorEl={moreAnchor}
                open={Boolean(moreAnchor)}
                onClose={() => setMoreAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                {visibleMore.map((item) => (
                  <MenuItem
                    key={item.path}
                    onClick={() => handleNav(item.path)}
                    selected={isActive(item.path)}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          )}

          {isMobile && <Box sx={{ flex: 1 }} />}

          {/* ── Notification bell ── */}
          <NotificationBell />

          {/* ── Cart icon ── */}
          <IconButton
            onClick={() => setCartOpen(true)}
            aria-label="shopping cart"
            sx={{ color: brand.navy }}
          >
            <Badge
              badgeContent={cartItemCount}
              color="error"
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: 10,
                  fontWeight: 700,
                  minWidth: 18,
                  height: 18,
                  padding: '0 4px',
                },
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 22 }} />
            </Badge>
          </IconButton>

          {/* ── Account avatar + menu ── */}
          <IconButton
            onClick={(e) => setAccountAnchor(e.currentTarget)}
            aria-label="account menu"
          >
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontSize: 13,
                fontWeight: 600,
                background: `linear-gradient(135deg, ${brand.violet} 0%, ${brand.violetSoft} 100%)`,
              }}
            >
              {user?.username?.[0]?.toUpperCase() || '?'}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={accountAnchor}
            open={Boolean(accountAnchor)}
            onClose={() => setAccountAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ px: 2, py: 1, minWidth: 240 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user?.username}
              </Typography>
              <Typography variant="caption" sx={{ color: brand.inkSoft, display: 'block' }}>
                {user?.email}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: brand.navy, display: 'block', mt: 0.5, fontWeight: 600 }}
              >
                {user?.tenantName}
                {user?.tenantType ? ` · ${user.tenantType}` : ''}
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {user?.roles.map((r) => (
                  <Typography
                    key={r}
                    variant="caption"
                    sx={{ mr: 1, color: brand.violet, fontWeight: 600 }}
                  >
                    {r}
                  </Typography>
                ))}
              </Box>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setAccountAnchor(null);
                navigate('/profile');
              }}
            >
              <PersonIcon fontSize="small" sx={{ mr: 1 }} />
              Edit profile
            </MenuItem>
            {user?.tenantType === 'WHOLESALER' && (
              <MenuItem
                onClick={() => {
                  setAccountAnchor(null);
                  navigate('/settings');
                }}
              >
                <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
                Settings
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setAccountAnchor(null);
                setChangePasswordOpen(true);
              }}
            >
              <LockResetIcon fontSize="small" sx={{ mr: 1 }} />
              Reset Password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />

      {/* Mobile drawer */}
      <Drawer
        anchor="left"
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { width: 260, boxSizing: 'border-box' },
        }}
      >
        <Toolbar sx={{ minHeight: 58 }}>
          <BrandMark
            onClick={() => {
              navigate('/dashboard');
              setMobileOpen(false);
            }}
          />
        </Toolbar>
        <Divider />
        <List>
          {visiblePrimary.map((item) => (
            <React.Fragment key={item.path}>
              {item.path === '/orders' && showMyOrdersDropdown ? (
                <>
                  <ListItemButton selected={isActive(item.path)} onClick={() => handleMyOrdersNav('date')}>
                    <ListItemText primary={item.label} secondary="Date Wise" />
                  </ListItemButton>
                  <ListItemButton onClick={() => handleMyOrdersNav('supplier')} sx={{ pl: 4 }}>
                    <ListItemText primary="Supplier Wise" secondary="My Orders" />
                  </ListItemButton>
                  <ListItemButton onClick={() => handleMyOrdersNav('product')} sx={{ pl: 4 }}>
                    <ListItemText primary="Product Wise" secondary="My Orders" />
                  </ListItemButton>
                </>
              ) : (
                <ListItemButton
                  selected={isActive(item.path)}
                  onClick={() => {
                    if (item.path === '/orders') handleBookOrder(0);
                    else handleNav(item.path);
                  }}
                >
                  <ListItemText primary={item.label} />
                </ListItemButton>
              )}
              {item.path === '/orders' && showBookOrder && (
                <>
                  <ListItemButton onClick={() => handleBookOrder(1)} sx={{ pl: 4 }}>
                    <ListItemText primary="Place Order" secondary="Book Order" />
                  </ListItemButton>
                  <ListItemButton onClick={() => handleBookOrder(2)} sx={{ pl: 4 }}>
                    <ListItemText primary="Order Templates" secondary="Book Order" />
                  </ListItemButton>
                </>
              )}
            </React.Fragment>
          ))}
          {visibleMore.length > 0 && <Divider sx={{ my: 1 }} />}
          {visibleMore.map((item) => (
            <ListItemButton
              key={item.path}
              selected={isActive(item.path)}
              onClick={() => handleNav(item.path)}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      {/* Cart drawer */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />

      {/* Main content */}
      <Box
        component="main"
        sx={{
          mt: { xs: '51px', md: '58px' },
          p: { xs: 1, md: 1.5 },
          minHeight: 'calc(100vh - 58px)',
          overflowX: 'auto',
        }}
      >
        <Outlet />
      </Box>

      {/* Subscription dialog — shown every login + every hour for wholesalers */}
      {isWholesaler && (
        <Dialog
          open={subReminderOpen}
          onClose={subGate.isBlocked ? undefined : () => { localStorage.setItem(SUB_REMINDER_LS_KEY, String(Date.now())); setSubReminderOpen(false); }}
          disableEscapeKeyDown={subGate.isBlocked}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: 15, color: subGate.isBlocked ? '#dc2626' : '#b45309' }}>
            {subGate.isBlocked ? '🔒 Subscription Overdue' : '⚠ Subscription Invoice Due'}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: 13, color: '#374151', mb: 1.5 }}>
              You have unpaid subscription invoice(s) totalling{' '}
              <strong>₹{subGate.dueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>.
            </Typography>
            {subGate.isBlocked ? (
              <>
                <Typography sx={{ fontSize: 13, color: '#dc2626', fontWeight: 700, mb: 1 }}>
                  Your subscription invoice is overdue by {subGate.daysSince} day{subGate.daysSince !== 1 ? 's' : ''}.
                  Order processing is blocked.
                </Typography>
                <Typography sx={{ fontSize: 13, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1, px: 1.5, py: 1, color: '#991b1b' }}>
                  Please contact <strong>Ardinotech Ltd</strong> for support or make the payment immediately to restore access.
                </Typography>
              </>
            ) : (() => {
              const daysLeft = Math.max(0, (32 + subGate.extensionDays) - subGate.daysSince);
              const blockMsg = daysLeft <= 1 ? 'tomorrow' : `in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
              return (
                <Typography sx={{ fontSize: 13, color: '#374151', mb: 1 }}>
                  Orders will be blocked <strong>{blockMsg}</strong> if unpaid.
                  {subGate.extensionDays > 0 && ` (Admin granted ${subGate.extensionDays}-day extension.)`}
                </Typography>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
            {!subGate.isBlocked && (
              <Button size="small" onClick={() => { localStorage.setItem(SUB_REMINDER_LS_KEY, String(Date.now())); setSubReminderOpen(false); }}>Remind Later</Button>
            )}
            <Button size="small" variant="contained"
              sx={{ bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}
              onClick={() => { localStorage.setItem(SUB_REMINDER_LS_KEY, String(Date.now())); setSubReminderOpen(false); navigate('/my-subscription'); }}>
              Pay Now
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
